import { z } from 'zod';

/**
 * Het auditspoor (Stap 18): één regel per beheerdershandeling.
 *
 * Geen berichttekst, geen PIN, geen token: de mapper per event kiest wat er in `details` komt, en
 * dat zijn ids, titels en aantallen.
 */
export const auditEntrySchema = z.object({
  id: z.guid(),
  occurredAt: z.string().datetime(),
  eventName: z.string(),
  actorType: z.enum(['person', 'platform_admin', 'system']),
  actorId: z.guid().nullable(),
  /** De naam van wie het deed, of "Systeem". */
  actorName: z.string(),
  objectType: z.string(),
  objectId: z.string().nullable(),
  /** In mensentaal: "Rol HR gewijzigd". */
  summary: z.string(),
  details: z.record(z.string(), z.unknown()),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

export const auditPageSchema = z.object({
  items: z.array(auditEntrySchema),
  nextCursor: z.string().nullable(),
});
export type AuditPage = z.infer<typeof auditPageSchema>;

export const auditQuerySchema = z.object({
  actorId: z.guid().optional(),
  objectType: z.string().max(60).optional(),
  objectId: z.string().max(80).optional(),
  /** Een eventnaam of het begin ervan: `communication.` of `access.role.changed.v1`. */
  event: z.string().max(80).optional(),
  from: z.string().date().optional(),
  to: z.string().date().optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

/**
 * Het auditspoor van het platform (AUD-1, ADR-0088): wat een platformbeheerder deed, en wat er
 * gebeurde zonder bedrijf - het platformmerk vooral. Dezelfde regel als in het spoor van een
 * bedrijf, plus het bedrijf waar de handeling over ging (als er een is). De naam van dat bedrijf
 * is die op het moment van de handeling: een bedrijf dat later een andere naam krijgt, verandert
 * het verleden niet.
 */
export const platformAuditEntrySchema = auditEntrySchema.extend({
  tenantId: z.guid().nullable(),
  tenantName: z.string().nullable(),
});
export type PlatformAuditEntry = z.infer<typeof platformAuditEntrySchema>;

export const platformAuditPageSchema = z.object({
  items: z.array(platformAuditEntrySchema),
  nextCursor: z.string().nullable(),
});
export type PlatformAuditPage = z.infer<typeof platformAuditPageSchema>;

/** De filters van het bedrijfsspoor, plus één bedrijf. */
export const platformAuditQuerySchema = auditQuerySchema.extend({
  tenantId: z.guid().optional(),
});
export type PlatformAuditQuery = z.infer<typeof platformAuditQuerySchema>;

// --- MST-8: de systeemstatus van het platform --------------------------------------------------

/** Eén event dat niet afgeleverd raakt: voor de lijst van fouten op de systeemstatus. */
export const outboxProblemSchema = z.object({
  id: z.guid(),
  eventName: z.string(),
  occurredAt: z.string().datetime(),
  attempts: z.number().int(),
  deadLetter: z.boolean(),
  lastError: z.string().nullable(),
});
export type OutboxProblem = z.infer<typeof outboxProblemSchema>;

/**
 * Hoe het platform er nu voor staat (MST-8). De worker heeft geen eigen endpoint; of hij werkt, blijkt
 * uit de outbox: staat het oudste niet-afgeleverde event er langer dan een paar minuten, dan hapert
 * hij.
 */
export const systemStatusSchema = z.object({
  checkedAt: z.string().datetime(),
  environment: z.string(),
  apiStartedAt: z.string().datetime(),
  nodeVersion: z.string(),
  modules: z.array(z.string()),
  database: z.object({ ok: z.boolean(), latencyMs: z.number().int() }),
  outbox: z.object({
    pending: z.number().int(),
    oldestPendingAt: z.string().datetime().nullable(),
    deadLetters: z.number().int(),
    dispatchedLastHour: z.number().int(),
    workerHealthy: z.boolean(),
  }),
  mail: z.object({
    /** SMTP ingesteld; zonder gaat mail naar de log. */
    smtpConfigured: z.boolean(),
    from: z.string(),
    /** Leeg = alle adressen toegelaten. */
    allowedRecipients: z.array(z.string()),
    allowedTenantIds: z.array(z.string()),
  }),
  problems: z.array(outboxProblemSchema),
});
export type SystemStatus = z.infer<typeof systemStatusSchema>;
